import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneGroupHandle } from './scene-group-addressing';
import { VideoStateTool } from './VideoStateTool';

import { removeLexicalEditor } from 'js/src/data/transcript/transcript-actions';
import { checkpointVideoOperation } from 'js/src/data/video-data';
import type { RootState } from 'js/src/libs/RootState';
import { CONTENT_SCENE_CATEGORIES } from 'js/src/luminary/consts';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { deleteSceneGroupOperation } from 'js/src/luminary/luminary-operations';
import {
    getPrePackagedSceneSequenceSceneGroupIds,
    getSceneGroupCategory,
} from 'js/src/luminary/luminary-selectors';

export const deleteSceneGroupSchema = z.object({
    scene_group_id: z.string(),
});
export type DeleteSceneGroupArgs = z.infer<typeof deleteSceneGroupSchema>;

export class DeleteSceneGroupTool extends VideoStateTool<typeof deleteSceneGroupSchema> {
    protected override readonly schema = deleteSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.deleteSceneGroup;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        'Deletes a scene group and every scene it contains.';

    public override getToolDisplayName(): string {
        return 'Deleting scene group';
    }

    public get statusIcon(): string {
        return 'delete';
    }

    public async execute(args: DeleteSceneGroupArgs, context: ToolExecutionContext) {
        const { parentActionGroup } = context;

        try {
            const { sceneGroup, sceneGroupShortId } = resolveSceneGroupHandle(this.luminary, args);

            // A pre-packaged scene sequence is a chain of scene groups that morph into one another;
            // they are authored as a unit and deleting only part of one leaves a broken morph, so
            // the whole chain goes together (same as the editor's delete button).
            const chainIds = getPrePackagedSceneSequenceSceneGroupIds(this.luminary, sceneGroup.id);
            const chainIdSet = new Set(chainIds);

            // The content-category guard below doesn't fire for an outro/brand-only video, so
            // without this the delete empties the video and Luminary throws "No scenes in video".
            if (chainIds.length >= this.luminary.sceneGroups.length) {
                return `Error: Scene group ${sceneGroupShortId} is the only scene group, so it can't be deleted. Add another scene or scene group first.`;
            }

            // Pre-flight the "must keep one content scene group" rule so the agent gets an
            // actionable message instead of the operation's raw `not_enough_scenes` throw. The
            // whole chain is leaving, so none of it can be what remains.
            const isDeletingLastContentGroup =
                CONTENT_SCENE_CATEGORIES.includes(getSceneGroupCategory(sceneGroup)!) &&
                !this.luminary.sceneGroups.some(
                    (group) =>
                        !chainIdSet.has(group.id) &&
                        CONTENT_SCENE_CATEGORIES.includes(getSceneGroupCategory(group)!),
                );
            if (isDeletingLastContentGroup) {
                return `Error: Scene group ${sceneGroupShortId} is the only content scene group, so it can't be deleted. Add another scene or scene group first.`;
            }

            const deletedSceneCount = chainIds.reduce(
                (total, chainId) =>
                    total + (this.luminary.getSceneGroupById(chainId)?.scenes.length ?? 0),
                0,
            );

            const { deleteSceneGroup, checkpointVideo } = makeOperationsCallable(
                { deleteSceneGroupOperation, checkpointVideoOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            // deleteSceneGroup leaves a voice-driven group's transcript editor behind, so remove it here.
            for (const chainId of chainIds) {
                deleteSceneGroup(chainId, false, parentActionGroup);
                this.dispatch(removeLexicalEditor(chainId));
            }
            checkpointVideo({ systemTriggered: false, parentActionGroup });

            const sceneCountNote =
                deletedSceneCount === 1 ? '1 scene' : `${deletedSceneCount} scenes`;
            const chainNote =
                chainIds.length > 1
                    ? ` (a linked sequence of ${chainIds.length} scene groups was removed together)`
                    : '';
            return `Success: Deleted scene group ${sceneGroupShortId} and its ${sceneCountNote}${chainNote}. The video now has ${this.luminary.sceneGroups.length} scene groups and ${this.luminary.scenes.length} scenes.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error deleting scene group: ${errorMessage}`;
        }
    }
}
